package org.wordsapp.ui.screen

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.wordsapp.currentTimeMillis
import org.wordsapp.model.SortOrder
import org.wordsapp.theme.*
import org.wordsapp.ui.component.VerticalListScrollbar
import org.wordsapp.ui.component.WordStatCard
import org.wordsapp.viewmodel.WordViewModel

@Composable
fun StatisticsScreen(viewModel: WordViewModel, onShowWeightDistribution: () -> Unit) {
    val words by viewModel.words.collectAsState()
    var sortOrder by remember { mutableStateOf(SortOrder.BY_WEIGHT) }

    val now = remember(words) { currentTimeMillis() }

    val sortedWords = remember(words, sortOrder) {
        when (sortOrder) {
            SortOrder.BY_WEIGHT -> words.sortedByDescending { it.weight(now) }
            SortOrder.BY_ATTEMPTS -> words.sortedByDescending { it.totalAttempts }
            // Failure tab: worst success rate first, but push never-attempted words to the bottom.
            SortOrder.BY_SUCCESS_RATE -> words.sortedWith(
                compareBy({ it.totalAttempts == 0 }, { it.successRate }),
            )
        }
    }

    val totalWords = words.size
    val dueWords = words.count { it.isDue(now) }
    val totalAttempts = words.sumOf { it.totalAttempts }
    val avgSuccessRate = if (words.isNotEmpty()) {
        words.filter { it.totalAttempts > 0 }
            .takeIf { it.isNotEmpty() }
            ?.let { practiced -> practiced.sumOf { it.successRate } / practiced.size }
            ?: 0.0
    } else 0.0

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = "Statistics",
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
            )
            TextButton(onClick = onShowWeightDistribution) {
                Text("Weights")
            }
        }

        Spacer(Modifier.height(16.dp))

        // Summary cards
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            SummaryCard("Words", totalWords.toString(), Blue500, Modifier.weight(1f))
            SummaryCard("Due", dueWords.toString(), Red500, Modifier.weight(1f))
            SummaryCard("Rate", "${(avgSuccessRate * 100).toInt()}%", Green500, Modifier.weight(1f))
            SummaryCard("Attempts", totalAttempts.toString(), Orange500, Modifier.weight(1f))
        }

        Spacer(Modifier.height(16.dp))

        // Sort tabs
        SingleChoiceSegmentedButtonRow(modifier = Modifier.fillMaxWidth()) {
            SortOrder.entries.forEachIndexed { index, order ->
                SegmentedButton(
                    selected = sortOrder == order,
                    onClick = { sortOrder = order },
                    shape = SegmentedButtonDefaults.itemShape(
                        index = index,
                        count = SortOrder.entries.size,
                    ),
                ) {
                    Text(
                        when (order) {
                            SortOrder.BY_WEIGHT -> "Chance"
                            SortOrder.BY_ATTEMPTS -> "Attempts"
                            SortOrder.BY_SUCCESS_RATE -> "Failure"
                        },
                        style = MaterialTheme.typography.labelSmall,
                    )
                }
            }
        }

        Spacer(Modifier.height(12.dp))

            // Selection draws due words strictly before not-yet-due ones, so the next-draw
        // probability is computed within the due tier; non-due words get 0 while any
        // word is due.
        val dueWeight = remember(words, now) { words.filter { it.isDue(now) }.sumOf { it.weight(now) } }
        val totalWeight = remember(words, now) { words.sumOf { it.weight(now) } }

        val listState = rememberLazyListState()
        Box(modifier = Modifier.fillMaxSize()) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize().padding(end = 12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                items(sortedWords, key = { it.id }) { word ->
                    val probability = when {
                        dueWeight > 0 -> if (word.isDue(now)) word.weight(now) / dueWeight else 0.0
                        totalWeight > 0 -> word.weight(now) / totalWeight
                        else -> 0.0
                    }
                    WordStatCard(word, pickProbability = probability, nowMs = now)
                }
            }
            VerticalListScrollbar(listState)
        }
    }
}

@Composable
private fun SummaryCard(
    label: String,
    value: String,
    color: androidx.compose.ui.graphics.Color,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = color.copy(alpha = 0.1f)),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = value,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = color,
            )
            Text(
                text = label,
                style = MaterialTheme.typography.labelSmall,
                color = color.copy(alpha = 0.8f),
            )
        }
    }
}
